<script lang="ts">
	import { page } from '$app/stores';
	import ArticleCard from '$lib/components/Blog/ArticleCard.svelte';
	import Tag from '$lib/components/Blog/Tags/Tag.svelte';
	import Breadcrumbs from '$lib/components/Global/Breadcrumbs/Breadcrumbs.svelte';
	import {
		getResourceUrlFromMeta,
		type CrumbItem
	} from '$lib/components/Global/Breadcrumbs/breadcrumbs';
	import Title from '$lib/components/SEO/Title.svelte';
	import Description from '$lib/components/SEO/Description.svelte';
	import type { PageData } from './$types';
	import __ from '$lib/stores/TranslationStore';
	import ScrollButton from '$lib/components/Global/ScrollButton.svelte';
	import { onMount } from 'svelte';

	export let data: PageData;

	const titleLookup: Record<string, string> = {
		Highlights: $__('Destaques'),
		'Most Read': $__('Mais lidos'),
		Latest: $__('Mais recentes')
	};
	$: ({ meta, type, description, modifier } = data);
	const crumbs: CrumbItem[] = processCrumbs();
	let articles: IndexArticle[] = data.data;
	let nextPage = data.nextPage;
	let language = $page.data.language as string;

	function processCrumbs(): CrumbItem[] {
		const { meta } = data;
		const initialCrumb: CrumbItem = { label: $__('Artigos'), url: '/artigos' };

		if (type === 'tag') return [initialCrumb, { label: 'Tags' }];

		if (!meta || !meta.context) return [initialCrumb];

		return [
			initialCrumb,
			...meta.context
				.filter((item) => item.label != 'Home')
				.map((item) => ({
					label: item.label,
					url: getResourceUrlFromMeta(item.resource, meta)
				}))
		];
	}

	let metaTitle: string;
	let metaDescription: string;
	$: {
		let mainTitle = '';
		let category = null;
		switch (type) {
			case 'categoria':
				mainTitle = titleLookup[meta?.resourceTitle]?.trim();
				category = description !== 'artigos' ? meta?.context[0]?.label : null;
				metaTitle = `${
					mainTitle + (!category ? '' : ' - ' + category)
				}: Conselhos Práticos e Análises Detalhadas na Poupança no Minuto`;

				metaDescription = `Sobre${!category ? '' : ' - ' + category}: ${titleLookup[
					meta?.resourceTitle
				]?.trim()}`;
				break;
			case 'artigo':
				mainTitle = 'Artigos relacionados';
				metaTitle = mainTitle + ': ' + meta?.resourceTitle?.trim();

				metaDescription = 'Artigos relacionados: ' + meta?.context[2]?.description || '';
				break;
			case 'tag':
				let tag = meta?.resourceTitle?.trim();
				metaTitle = tag + ': Descubra os Últimos Artigos e Tendências';
				metaDescription =
					'Explorando ' +
					tag +
					': Acompanhe uma coleção selecionada dos melhores artigos, análises e insights. Atualize-se com as novidades mais recentes e profundas sobre ' +
					tag +
					' no nosso site.';
				break;
			default:
				metaTitle = 'Página sobre: ' + meta?.resourceTitle?.trim();
				metaDescription = 'Detalhes: ' + meta?.resourceTitle?.trim();
				break;
		}
	}

	let root: HTMLDivElement;
	let footer: HTMLElement;

	onMount(() => {
		let observer = new IntersectionObserver(handleScroll, {
			threshold: 1.0
		});
		observer.observe(footer);
	});

	function handleScroll(entries: IntersectionObserverEntry[]) {
		try {
			entries.forEach(async (entry) => {
				if (!nextPage || !entry.isIntersecting) {
					return;
				}
				const res = await fetch(
					`/api/blog/${type}/${description}${
						modifier ? '/' + modifier : ''
					}/loadmore?${new URLSearchParams({
						page: nextPage.toString(),
						...(language != 'pt' ? { lang: language } : {})
					})}`
				);
				const resData = await res.json();
				articles = [...articles, ...resData.data];
				nextPage = resData.hasNext ? nextPage + 1 : null;
			});
		} catch (error) {}
	}
</script>

<svelte:head>
	<Title content={metaTitle} />
	<Description content={metaDescription} />
</svelte:head>

<div class="container mx-auto space-y-8 bg-white pb-7 pt-12 text-dark" bind:this={root}>
	<header class="flex flex-col gap-y-6 bg-white">
		<Breadcrumbs
			class="text-xs text-dark"
			{crumbs}
			arrowClass="h-3 w-4 stroke-dark stroke-[10px]"
		/>
		{#if modifier === 'relacionados'}
			<h1 class="my-0 flex flex-col gap-2 text-2xl font-bold md:block">
				<span>{$__('Relacionados com')}:</span>
				<a
					class="text-primary no-underline visited:text-secondary hover:underline md:ml-2"
					href="{$page.data?.location?.origin}/artigos/{meta.context[0].slug}/{description}"
				>
					"{meta?.resourceTitle}"
				</a>
			</h1>
		{:else if type === 'tag'}
			<div class="my-0 flex flex-row flex-wrap items-center gap-4">
				<h1 class="text-2xl font-bold">{$__('Artigos sobre')}:</h1>
				<Tag class="bg-primary text-white">{meta?.resourceTitle}</Tag>
			</div>
		{:else}
			<h1 class="my-0 text-2xl font-bold">
				{titleLookup[meta?.resourceTitle] || meta?.resourceTitle}
			</h1>
		{/if}
	</header>
	<main
		class="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-10 lg:grid-cols-3 xl:gap-x-16 xl:gap-y-10"
	>
		{#each articles as article}
			<ArticleCard
				{article}
				descriptionClass="line-clamp-3 overflow-hidden text-ellipsis text-justify text-sm"
			/>
		{/each}
	</main>
	<footer bind:this={footer} />
</div>
<ScrollButton />
